import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCrown,
  faSyncAlt,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import Content from "../../Components/Core/Content";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";
import {
  getSubscriptionStatus,
  getSubscriptionPlans,
  requestSubscriptionPayment,
} from "../../Services/SubscriptionService";
import { redirectToPaymentGateway } from "../../Services/paymentRedirect";

const SubscriptionManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewLoading, setRenewLoading] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    document.title = "مدیریت اشتراک";
    fetchData();
  }, []);

  useEffect(() => {
    const result = searchParams.get("result");
    if (result === "success") {
      setSnackbarMessage("اشتراک با موفقیت تمدید شد.");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setSearchParams({});
      fetchData();
    } else if (result === "fail") {
      setSnackbarMessage("پرداخت ناموفق بود یا لغو شد.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, plansRes] = await Promise.all([
        getSubscriptionStatus(),
        getSubscriptionPlans(),
      ]);
      setStatus(statusRes);
      setPlans(plansRes || []);
    } catch (error) {
      setSnackbarMessage(error?.message || "خطا در دریافت اطلاعات اشتراک");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) => {
    if (amount == null) return "۰";
    return new Intl.NumberFormat("fa-IR").format(amount);
  };

  const handleRenew = async (planId) => {
    if (!status?.canRenew) {
      setSnackbarMessage(
        status?.renewalBlockReason || "امکان تمدید در حال حاضر وجود ندارد",
      );
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    try {
      setRenewLoading(planId);
      const response = await requestSubscriptionPayment(planId);
      if (response?.authority) {
        redirectToPaymentGateway(response);
      } else {
        setSnackbarMessage("خطا در ایجاد درخواست پرداخت");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage(error?.message || "خطا در درخواست پرداخت");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setRenewLoading(null);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid mb-4">
      <Content>
        <div className="subscription-page">
          <style>{`
            .subscription-page {
              min-height: 60vh;
              font-family: "MyPersianFont", "iran-sans", sans-serif;
              direction: rtl;
              padding: 20px;
              max-width: 1400px;
              margin: 0 auto;
            }
            
            .subscription-header {
              margin-bottom: 32px;
              padding-bottom: 24px;
              border-bottom: 2px solid rgba(124, 200, 144, 0.2);
            }
            
            .subscription-title {
              font-size: 32px;
              font-weight: 700;
              color: #053251;
              margin: 0 0 10px 0;
            }
            
            .subscription-subtitle {
              font-size: 16px;
              color: #6c757d;
              margin: 0;
            }
            
            .status-card {
              background: white;
              border-radius: 20px;
              padding: 32px 40px;
              box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
              border: 2px solid #f0f0f0;
              margin-bottom: 40px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              flex-wrap: wrap;
              gap: 24px;
              transition: all 0.3s ease;
            }
            
            .status-card:hover {
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
            }
            
            .status-card.active {
              border-color: rgba(124, 200, 144, 0.5);
              background: linear-gradient(135deg, #f8fdf9 0%, #fff 100%);
            }
            
            .status-card.inactive {
              border-color: rgba(220, 53, 69, 0.3);
              background: linear-gradient(135deg, #fff8f8 0%, #fff 100%);
            }
            
            .status-info {
              display: flex;
              align-items: center;
              gap: 20px;
            }
            
            .status-icon {
              width: 64px;
              height: 64px;
              border-radius: 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 28px;
              flex-shrink: 0;
            }
            
            .status-icon.active {
              background: rgba(124, 200, 144, 0.25);
              color: #2e7d32;
            }
            
            .status-icon.inactive {
              background: rgba(220, 53, 69, 0.2);
              color: #c62828;
            }
            
            .status-days {
              font-size: 32px;
              font-weight: 700;
              color: #053251;
              line-height: 1.2;
            }
            
            .status-label {
              font-size: 15px;
              color: #6c757d;
              margin-top: 6px;
            }
            
            .status-badge {
              padding: 8px 18px;
              border-radius: 24px;
              font-size: 14px;
              font-weight: 600;
              white-space: nowrap;
            }
            
            .status-badge.active {
              background: rgba(124, 200, 144, 0.25);
              color: #2e7d32;
            }
            
            .status-badge.inactive {
              background: rgba(220, 53, 69, 0.2);
              color: #c62828;
            }
            
            .plans-section-title {
              font-size: 22px;
              font-weight: 600;
              color: #053251;
              margin-bottom: 24px;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            
            .plans-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
              gap: 24px;
            }
            
            @media (min-width: 1200px) {
              .plans-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }
            
            @media (min-width: 1600px) {
              .plans-grid {
                grid-template-columns: repeat(4, 1fr);
              }
            }
            
            .plan-card {
              background: white;
              border-radius: 20px;
              padding: 28px;
              box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
              border: 2px solid #f0f0f0;
              transition: all 0.3s ease;
              display: flex;
              flex-direction: column;
            }
            
            .plan-card:hover {
              transform: translateY(-6px);
              box-shadow: 0 12px 32px rgba(124, 200, 144, 0.2);
              border-color: rgba(124, 200, 144, 0.4);
            }
            
            .plan-card-header {
              display: flex;
              align-items: center;
              gap: 14px;
              margin-bottom: 20px;
            }
            
            .plan-icon {
              width: 52px;
              height: 52px;
              border-radius: 14px;
              background: rgba(124, 200, 144, 0.25);
              color: #2e7d32;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              flex-shrink: 0;
            }
            
            .plan-title {
              font-size: 20px;
              font-weight: 600;
              color: #053251;
              line-height: 1.3;
            }
            
            .plan-days {
              font-size: 14px;
              color: #6c757d;
              margin-top: 4px;
            }
            
            .plan-price {
              font-size: 26px;
              font-weight: 700;
              color: #053251;
              margin: 16px 0 20px;
            }
            
            .plan-price span {
              font-size: 15px;
              font-weight: 500;
              color: #6c757d;
            }
            
            .plan-desc {
              font-size: 14px;
              color: #6c757d;
              margin-bottom: 24px;
              flex-grow: 1;
              line-height: 1.6;
            }
            
            .plan-btn {
              width: 100%;
              padding: 14px 24px;
              border-radius: 14px;
              border: none;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              font-family: "MyPersianFont", "iran-sans", sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              transition: all 0.3s ease;
              background: linear-gradient(135deg, #7cc890 0%, #5fa872 100%);
              color: white;
            }
            
            .plan-btn:hover:not(:disabled) {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(124, 200, 144, 0.5);
              background: linear-gradient(135deg, #6bb780 0%, #4e9762 100%);
            }
            
            .plan-btn:active:not(:disabled) {
              transform: translateY(0);
            }
            
            .plan-btn:disabled {
              background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%) !important;
              color: #6c757d !important;
              cursor: not-allowed !important;
              opacity: 0.7;
            }
            
            .plan-btn:disabled:hover {
              transform: none !important;
              box-shadow: none !important;
            }
            
            .empty-plans {
              text-align: center;
              color: #6c757d;
              padding: 60px 20px;
              font-size: 16px;
            }
            
            /* Responsive adjustments */
            @media (max-width: 768px) {
              .subscription-page {
                padding: 16px;
              }
              
              .subscription-title {
                font-size: 24px;
              }
              
              .status-card {
                padding: 24px;
              }
              
              .status-icon {
                width: 56px;
                height: 56px;
                font-size: 24px;
              }
              
              .status-days {
                font-size: 26px;
              }
              
              .plans-grid {
                grid-template-columns: 1fr;
                gap: 16px;
              }
              
              .plan-card {
                padding: 24px;
              }
            }
          `}</style>

          <div className="subscription-header">
            <h1 className="subscription-title">مدیریت اشتراک</h1>
            <p className="subscription-subtitle">
              وضعیت اشتراک و تمدید سرویس داک لینک
            </p>
          </div>

          <div
            className={`status-card ${status?.isActive ? "active" : "inactive"}`}
          >
            <div className="status-info">
              <div
                className={`status-icon ${status?.isActive ? "active" : "inactive"}`}
              >
                <FontAwesomeIcon
                  icon={status?.isActive ? faCheckCircle : faExclamationCircle}
                />
              </div>
              <div>
                <div className="status-days">
                  {status?.totalDayRemaining != null
                    ? new Intl.NumberFormat("fa-IR").format(
                        status.totalDayRemaining,
                      )
                    : "۰"}{" "}
                  روز
                </div>
                <div className="status-label">روزهای باقی‌مانده از اشتراک</div>
              </div>
            </div>
            <span
              className={`status-badge ${status?.isActive ? "active" : "inactive"}`}
            >
              {status?.isActive ? "فعال" : "منقضی"}
            </span>
          </div>

          <div className="plans-section-title">
            <FontAwesomeIcon icon={faCrown} />
            <span>پلن‌های تمدید</span>
          </div>

          <div className="plans-grid">
            {plans.map((plan) => (
              <div key={plan.id} className="plan-card">
                <div className="plan-card-header">
                  <div className="plan-icon">
                    <FontAwesomeIcon icon={faCalendarCheck} />
                  </div>
                  <div>
                    <div className="plan-title">{plan.title}</div>
                    <div className="plan-days">
                      {new Intl.NumberFormat("fa-IR").format(plan.totalDay)}{" "}
                      روزه
                    </div>
                  </div>
                </div>
                <div className="plan-price">
                  {formatPrice(plan.price)}
                  <span> تومان</span>
                </div>
                {plan.description && (
                  <div className="plan-desc">{plan.description}</div>
                )}
                <button
                  type="button"
                  className="plan-btn"
                  onClick={() => handleRenew(plan.id)}
                  disabled={!status?.canRenew || renewLoading !== null}
                >
                  {renewLoading === plan.id ? (
                    <>
                      <FontAwesomeIcon icon={faSyncAlt} spin />
                      در حال انتقال...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSyncAlt} />
                      تمدید اشتراک
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          {plans.length === 0 && !loading && (
            <div className="empty-plans">
              در حال حاضر پلنی برای نمایش وجود ندارد.
            </div>
          )}
        </div>
      </Content>

      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </div>
  );
};

export default SubscriptionManagement;
